import { values, sum, keys, size } from 'lodash/fp';
import React, { useMemo } from 'react';
import { AutoSizer } from 'react-virtualized';
import { Table } from 'reactstrap';
import { Bar, BarChart, CartesianGrid, Tooltip, YAxis } from 'recharts';
import styled from 'styled-components';

import { EmptyState } from '@portals/table';
import { useTheme } from '@portals/ui';
import { getStyledThemeColor, hexToRgba } from '@portals/utils';

import { StatCardContainer } from './common';

const DevicesByOrg = ({ orgs }) => {
  const { color } = useTheme();
  const colors = useMemo(
    () =>
      keys(orgs).map((_, index) =>
        hexToRgba(color.green, 100 - (60 / size(keys(orgs))) * index)
      ),
    [color.green, orgs]
  );

  const hasNoDevices = sum(values(orgs)) === 0;

  if (hasNoDevices) {
    return (
      <Container>
        <div className="header">
          <div className="title">Devices by Organization</div>
        </div>

        <EmptyState
          label="No available devices data"
          src="empty-state-widgets"
        />
      </Container>
    );
  }

  return (
    <Container>
      <div className="header">
        <div className="title">Devices by Organization</div>
      </div>

      <div className="chart-wrapper">
        <AutoSizer>
          {({ height, width }) => (
            <BarChart
              width={width}
              height={height}
              data={[orgs]}
              barSize={20}
              barGap={20}
            >
              <CartesianGrid vertical={false} stroke={color.gray300} />
              <YAxis
                tickSize={0}
                tickMargin={20}
                width={40}
                axisLine={false}
                allowDecimals={false}
              />
              <Tooltip
                labelFormatter={() => 'Connected Devices'}
                cursor={false}
              />

              {keys(orgs).map((model, index) => (
                <Bar
                  key={model}
                  dataKey={model}
                  barSize={20}
                  minPointSize={-1}
                  radius={[4, 4, 0, 0]}
                  fill={colors[index]}
                />
              ))}
            </BarChart>
          )}
        </AutoSizer>
      </div>

      <Table className="mb-0">
        <thead>
          <tr>
            <th>Organization</th>
            <th className="text-center">Devices</th>
          </tr>
        </thead>
        <tbody>
          {keys(orgs).map((name, index) => (
            <tr key={name}>
              <td>
                <div
                  className="legend-circle"
                  style={{ background: colors[index] }}
                />
                {name}
              </td>
              <td className="text-center">{orgs[name]}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Container>
  );
};

const Container = styled(StatCardContainer)`
  grid-template-rows: max-content 200px 1fr;
  grid-row-gap: 35px;

  .chart-wrapper {
    height: 200px;

    .recharts-bar-rectangle {
      path {
        filter: drop-shadow(0px 2px 2px ${getStyledThemeColor('gray400')});
      }
    }
  }

  table {
    height: fit-content;

    tr {
      td {
        .legend-circle {
          display: inline-block;
          height: 10px;
          width: 10px;
          border-radius: 50%;
          margin-right: 10px;
        }
      }
    }
  }
`;

export default DevicesByOrg;
